/**
 * 节点包的**装卸与落盘**。
 *
 * ## 落盘存什么
 *
 * 存**状态**，不存全部内容：
 *
 * ```ts
 * { extra: PackageFile[], disabled: string[] }
 * ```
 *
 *   · `extra`   —— 用户导入的包（内置的跟着程序走，不用存，存了反而会
 *                  在升级程序之后用旧版本覆盖新版本）；
 *   · `disabled` —— 被**卸载的内置包 id**。卸载内置包必须记下来，
 *                  否则刷新之后它又回来了（用户会以为卸载没生效）。
 *
 * ⚠️ 不存「全部已安装包」的原因：内置包的内容属于**程序版本**，
 * 不属于用户数据。存下来会让「程序升级了包，用户机器上还是旧的」。
 *
 * ## 失败模式
 *
 * 落盘可能失败（配额满 / 隐私模式），按仓库里其它几处的做法**静默吞掉** ——
 * 记不住包列表不该让编辑器报错。但**导入校验失败必须报**：
 * 那是用户主动做的操作，他要看到为什么不行。
 */
import { computed, ref } from 'vue'
import { builtInPackageFiles, builtInPackages, packagesVersion, setPackages } from '@/playground/nodeTypes'
import type { NodePackage } from '@/node/package'
import {
  packageFileName,
  parsePackageFile,
  toPackageFile,
  type PackageFile,
} from '@/node/packageFile'

const STORAGE_KEY = 'node-print.packages.v1'

interface PackageState {
  /** 用户导入的包 */
  extra: PackageFile[]
  /** 被卸载的内置包 id */
  disabled: string[]
}

function readState(): PackageState {
  const empty: PackageState = { extra: [], disabled: [] }
  if (typeof localStorage === 'undefined') return empty
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return empty
    const parsed = JSON.parse(raw) as Partial<PackageState>
    return {
      extra: Array.isArray(parsed.extra) ? (parsed.extra as PackageFile[]) : [],
      disabled: Array.isArray(parsed.disabled) ? (parsed.disabled as string[]) : [],
    }
  } catch {
    /* 存坏了就当没存过 —— 一个坏字段不该让编辑器起不来 */
    return empty
  }
}

function writeState(state: PackageState): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    /* 配额满 / 隐私模式：记不住包列表不该报错 */
  }
}

export interface InstalledPackageInfo {
  id: string
  label: string
  /** 内置包不能删，只能卸载（禁用） */
  builtIn: boolean
  /**
   * **能不能卸载** —— 也就是它是不是 `domain`（可选加载的领域能力）。
   *
   * ⚠️ `base`（核心基础能力：函数节点那种，`kind: 'base'`）和 `core`（内核）
   * 都**不可卸载**：用户能把编辑器的基本功卸掉，只会得到一堆算不出值的节点。
   * 「节点包」菜单据此分两栏：可装卸的，和"随程序来的"。
   */
  optional: boolean
  enabled: boolean
  /**
   * **能不能导出成文件** —— 也就是它有没有"文件形态"。
   *
   * ⚠️ 代码包（打印包那种带组件 / TS 求值器的）**没有**：`JSON.stringify`
   * 会把组件和函数静默丢掉，导回来还会被 `parsePackageFile` 拒绝，
   * 所以菜单里**不该给它「导出」入口** —— 一个"看起来成功但没用"的入口
   * 比没有入口更坏（用户会以为导出成功了）。
   */
  exportable: boolean
  categories: number
  nodeTypes: number
}

/**
 * 菜单眼里「一个已安装的包」—— 只要这几行事实。
 *
 * ⚠️ 从 `InstalledPackageInfo` 里 Pick，**不在菜单那一侧再写一份形状**：
 * 两份形状迟早漂，而漂的表现是「菜单里读到的 `optional` 是 undefined」——
 * 那一栏就会**悄悄列错**（可卸载的和不可卸载的混在一起）。
 */
export type InstalledPackageView = Pick<
  InstalledPackageInfo,
  'id' | 'label' | 'builtIn' | 'optional' | 'enabled' | 'exportable' | 'nodeTypes'
>

/** 一个包在菜单里要显示的那几行事实 */
function infoOf(
  pack: NodePackage,
  options: { builtIn: boolean; disabled: ReadonlySet<string>; exportable: boolean },
): InstalledPackageInfo {
  const optional = pack.kind === 'domain'
  return {
    id: pack.id,
    label: pack.label,
    builtIn: options.builtIn,
    optional,
    exportable: options.exportable,
    /* ⚠️ 不可卸载的包**永远**是启用状态（`disabled` 对它无效） */
    enabled: !optional || !options.disabled.has(pack.id),
    categories: pack.categories.length,
    nodeTypes: pack.nodeTypes.length,
  }
}

export function usePackages(ctx: { showNotice: (text: string) => void }) {
  const state = ref<PackageState>(readState())
  /** 上一次操作的问题（导入失败时给人看） */
  const problems = ref<string[]>([])

  /**
   * 把「当前应该装什么」推给注册表。
   *
   * ⚠️ 这里**只说额外装了什么、关掉了哪些** —— 内置包由 `setPackages`
   * 自己拼（见那边的注释：让调用方记得带内置包是个迟早被用错的 API）。
   */
  function apply(): void {
    setPackages({ extra: state.value.extra.map((file) => file.package), disabled: state.value.disabled })
  }

  function persist(): void {
    writeState(state.value)
  }

  /**
   * 清单：内置的 + 导入的，含**能不能卸载**（给「节点包」菜单用）。
   *
   * ⚠️ 三档在一份清单里（见 `node/package.ts` 的 `kind` 表）：
   *   · `core` / `base` → `optional: false`（随程序来的，只读一行）
   *   · `domain`        → `optional: true`（能装能卸）
   * 菜单按 `optional` 分栏，不按"是不是内置" —— 内置的可选包（数据源）
   * 照样能卸。
   */
  const installed = computed<InstalledPackageInfo[]>(() => {
    /* ⚠️ 读版本号，装卸之后这个 computed 才会重算 */
    void packagesVersion.value
    const disabled = new Set(state.value.disabled)
    return [
      /*
       * ⚠️ 用 `builtInPackages()`（代码包 + 数据包），**不是**
       * `builtInPackageFiles()` —— 后者只有数据包那一半。
       * 踩过：打印包（内置**代码**包）一度**根本不出现在这个清单里**，
       * 于是用户在「节点包」菜单里既看不到它也卸不掉它。
       */
      ...builtInPackages().map((pack) =>
        infoOf(pack, {
          builtIn: true,
          disabled,
          /* ⚠️ 内置包里只有**数据包**有文件形态（代码包导不出，见 `exportable`） */
          exportable: builtInPackageFiles().some((f) => f.package.id === pack.id),
        }),
      ),
      ...state.value.extra.map((file) =>
        /* 用户导入的一定是数据包（`parsePackageFile` 拒绝 `editors`/`evaluators`） */
        infoOf(file.package, { builtIn: false, disabled, exportable: true }),
      ),
    ]
  })

  /**
   * 导入一个包文件（内容已经 `JSON.parse` 过了）。
   *
   * 校验不过就**返回问题清单**，装不进去 —— 半坏的包进了注册表，
   * 会在别的地方以更难查的方式爆出来（比如菜单里一个点不动的项）。
   */
  function install(json: unknown): boolean {
    const parsed = parsePackageFile(json)
    if (!parsed.ok) {
      problems.value = parsed.problems
      ctx.showNotice(`节点包导入失败：${parsed.problems[0] ?? '格式不对'}`)
      return false
    }
    const id = parsed.file.package.id
    /*
     * ⚠️ 判据是 `builtInPackages()`（含**代码包**）。只查数据包文件的话，
     * 用户可以导入一个 id 叫 `print` 的包 —— 它的节点类型和内置打印包
     * 撞名，注册表当场抛错，**整个界面崩掉**（不是"导入失败"那种可控结果）。
     */
    if (builtInPackages().some((pack) => pack.id === id)) {
      problems.value = [`「${id}」是内置包，不能用一个同名的外部包装上去`]
      ctx.showNotice(`节点包「${id}」与内置包重名`)
      return false
    }
    if (state.value.extra.some((f) => f.package.id === id)) {
      problems.value = [`「${id}」已经装过了（先卸载再导入）`]
      ctx.showNotice(`节点包「${id}」已经装过了`)
      return false
    }

    state.value = {
      /* 同名包是「替换」，不是「再装一份」——重复的 id 注册表会抛错 */
      extra: [...state.value.extra.filter((f) => f.package.id !== id), parsed.file],
      disabled: state.value.disabled.filter((d) => d !== id),
    }
    problems.value = []
    persist()
    apply()
    ctx.showNotice(`已装上节点包「${parsed.file.package.label}」（${parsed.file.package.nodeTypes.length} 个节点）`)
    return true
  }

  /**
   * 卸载一个包（**只有 `domain` 能卸**）。
   *
   * ⚠️ **只影响注册表，不动画布上已有的节点**。
   * 文档里逐节点存了行结构，所以那些节点照样画得出来，
   * 只是算不出值了（求值层会给「未注册求值器」的诊断）。
   * 这是有意的：卸载一个包不该静默删掉用户图里的东西。
   *
   * ⚠️ **`base` / `core` 拒绝卸载**，而且要说清为什么：
   * 函数节点那种是核心基础能力（用户 2027 定的），
   * 卸掉它只会得到一堆算不出值的节点。菜单里本来就不给入口，
   * 这里是第二道门（菜单之外还能从别处调到）。
   */
  function uninstall(id: string): void {
    const info = installed.value.find((p) => p.id === id)
    if (info && !info.optional) {
      ctx.showNotice(`「${info.label}」是随程序来的核心能力，不能卸载`)
      return
    }
    /* ⚠️ 同 `install`：内置的判据含代码包（打印包就是），否则卸不掉它 */
    const builtIn = builtInPackages().some((pack) => pack.id === id)
    state.value = {
      extra: state.value.extra.filter((f) => f.package.id !== id),
      disabled: builtIn ? [...new Set([...state.value.disabled, id])] : state.value.disabled,
    }
    problems.value = []
    persist()
    apply()
    const label = info?.label ?? id
    ctx.showNotice(`已卸载节点包「${label}」（画布上已有的节点还在，但算不出值）`)
  }

  /** 重新启用一个被卸载的可选包 */
  function enable(id: string): void {
    state.value = { ...state.value, disabled: state.value.disabled.filter((d) => d !== id) }
    persist()
    apply()
    ctx.showNotice(`已重新启用节点包「${id}」`)
  }

  /**
   * 导出某个已安装的包（给人当模板改）。
   *
   * ⚠️ 菜单只给**可选包**列入口（基础包导出成 JSON 没有意义：
   * 它跟着程序走，改它得改代码）。这里不额外拦截 —— 多一道没人走到的
   * 分支不如少一道。
   */
  function exportPackage(id: string): void {
    /*
     * ⚠️ 只有**数据包**有文件形态：代码包（打印包）导出成 JSON 会把
     * 组件和 TS 求值器静默丢掉（`toPackageFile` 那边有专门的拒绝）。
     * 所以这里查的是文件那一半，代码包自然查不到 → 安静返回。
     */
    const file =
      builtInPackageFiles().find((f) => f.package.id === id) ??
      state.value.extra.find((f) => f.package.id === id)
    if (!file) return
    downloadJson(packageFileName(file.package), file)
    ctx.showNotice(`已导出节点包「${file.package.label}」`)
  }

  /**
   * 弹系统选文件框导入一个包。
   *
   * ⚠️ 用 `<input type=file>` 而不是 File System Access API（和打开模板一致）：
   * 后者只有 Chromium 系支持，而这个编辑器要能在多端跑通。
   */
  function importFromFile(): void {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'application/json,.json'
    input.style.display = 'none'
    input.addEventListener('change', () => {
      const file = input.files?.[0]
      input.remove()
      if (!file) return
      void file
        .text()
        .then((text) => {
          let json: unknown
          try {
            json = JSON.parse(text)
          } catch {
            problems.value = ['文件不是合法的 JSON']
            ctx.showNotice('节点包导入失败：文件不是合法的 JSON')
            return
          }
          install(json)
        })
        .catch(() => {
          problems.value = ['读不到文件内容']
          ctx.showNotice('节点包导入失败：读不到文件内容')
        })
    })
    input.click()
  }

  /** 起手装一次（首次启动时把内置包装上） */
  apply()

  return { installed, problems, install, importFromFile, uninstall, enable, exportPackage }
}

/**
 * 导出时的规范化：`format` / `version` 由 `toPackageFile` 统一填，
 * 免得手写的 JSON 漏掉它们（然后就再也导不回来了）。
 *
 * ⚠️ 这里**不**再手写 `format` —— 第一版写成 `{ format: PACKAGE_FORMAT, …toPackageFile(pack) }`，
 * 展开在后面把前面的覆盖掉，等于白写。TS 报了 TS2783 才对。
 */
export function normalizeForExport(pack: NodePackage): PackageFile {
  return toPackageFile(pack)
}

function downloadJson(name: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = name
  link.click()
  URL.revokeObjectURL(url)
}
